import { useToast } from '../../../../hooks/useToast';
import React, { useEffect, useState, useRef } from 'react';
import {
  Edit3,
  Layers,
  Plus,
  Shield,
  UserMinus,
  UserPlus,
  Users,
  X
} from 'lucide-react';
import { ModuleHeader } from '../../../../components/shared/ModuleHeader/ModuleHeader';
import { SidebarForm } from '../../../../components/shared/SidebarForm/SidebarForm';
import { DataTable, ColumnDef } from '../../../../components/shared/DataTable/DataTable';
import { TableSearch } from '../../../../components/shared/TableSearch/TableSearch';
import { MasterDetailLayout } from '../../../../components/shared/MasterDetail/MasterDetailLayout';
import { RowActionsMenu, viewAction, editAction, deleteAction } from '../../../../components/shared/RowActionsMenu/RowActionsMenu';
import { DetailPanel } from '../../../../components/shared/DetailPanel/DetailPanel';
import { DetailHeaderButton, DetailIdentity } from '../../../../components/shared/DetailPanel/DetailBlocks';
import { useClickOutside, isDetailKeepZone } from '../../../../hooks/useClickOutside';
import Modal from '../../../../components/shared/Modal/Modal';

import { GroupData } from '../../../../types/Group.types';
import { RoleData } from '../../../../types/Role.types';
import { UserData } from '../../../../types/User.types';
import { useApiRoles } from '../../../../hooks/useApiRoles';
import { useApiUsers } from '../../../../hooks/useApiUsers';
import { Skeleton } from '../../../../components/shared/Skeleton/Skeleton';
import './GroupsView.css';

interface GroupsViewProps {
  onMenuOpen: () => void;
  menuCloseSignal: number;
}

export const GroupsView = ({
  onMenuOpen,
  menuCloseSignal
}: GroupsViewProps) => {
  const { notify } = useToast();
  const [groups, setGroups] = useState<GroupData[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const { roles, loading: rolesLoading, error: rolesError } = useApiRoles();
  const { users, loading: usersLoading, error: usersError } = useApiUsers();
  const [selectedGroup, setSelectedGroup] = useState<GroupData | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [deleteModalGroup, setDeleteModalGroup] = useState<GroupData | null>(null);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddUserDropdownOpen, setIsAddUserDropdownOpen] = useState(false);
  const [isAddRoleDropdownOpen, setIsAddRoleDropdownOpen] = useState(false);

  useEffect(() => {
    const fetchGroups = async () => {
      setGroupsLoading(true);
      try {
        const Cookies = (await import('universal-cookie')).default;
        const cookies = new Cookies();
        const userCookie = cookies.get('101Obex');
        if (!userCookie || !userCookie.token) return;

        let url = `/list_groups?page=${currentPage}&page_size=50&access_token=${encodeURIComponent(userCookie.token)}`;
        if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;

        const ObexRequestsHandlerP80 = (await import('../../../../handlers/ObexRequestsHandlerP80')).default;
        let res = await ObexRequestsHandlerP80.get(url);
        if (res.data) res = res.data;

        const groupsData = res?.response ?? res?.result?.response;
        const rawList = Array.isArray(groupsData?.data) ? groupsData.data : (Array.isArray(groupsData) ? groupsData : []);

        const mappedGroups: GroupData[] = rawList.map((g: any) => {
          const groupId = String(g.id);
          return {
            id: groupId,
            name: g.name,
            description: g.description || '',
            roleId: '',
            userIds: [],
            role_names: g.role_names || '',
            members_count: Number(g.members_count || 0)
          };
        });

        setGroups(mappedGroups);
        setTotalRecords(groupsData?.total ?? mappedGroups.length);
      } catch (error) {
        console.error('Error fetching groups:', error);
      } finally {
        setGroupsLoading(false);
      }
    };

    fetchGroups();
  }, [searchQuery, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Roles and members caches for selected group details drawer.
  const [groupRolesById, setGroupRolesById] = useState<Record<string, RoleData[] | null>>({});
  const [groupMembersById, setGroupMembersById] = useState<Record<string, UserData[] | null>>({});

  const loadGroupDetailsIfNeeded = async (groupId: string) => {
    if (groupRolesById[groupId] !== undefined && groupMembersById[groupId] !== undefined) return;
    // Mark both as in-flight immediately so the eager preload below and a
    // row click firing around the same time don't kick off duplicate fetches.
    setGroupRolesById(prev => (prev[groupId] !== undefined ? prev : { ...prev, [groupId]: undefined as any }));
    setGroupMembersById(prev => (prev[groupId] !== undefined ? prev : { ...prev, [groupId]: undefined as any }));
    try {
      const Cookies = (await import('universal-cookie')).default;
      const cookies = new Cookies();
      const userCookie = cookies.get('101Obex');
      if (!userCookie || !userCookie.token) return;

      const ObexRequestsHandlerP80 = (await import('../../../../handlers/ObexRequestsHandlerP80')).default;
      let [rolesRes, usersRes] = await Promise.all([
        ObexRequestsHandlerP80.get('/groups-roles-list', { access_token: userCookie.token, group_id: groupId }),
        ObexRequestsHandlerP80.get('/groups-users-list', { access_token: userCookie.token, group_id: groupId })
      ]);
      if (rolesRes.data) rolesRes = rolesRes.data;
      if (usersRes.data) usersRes = usersRes.data;

      const rawRoles = rolesRes?.response ?? rolesRes?.result?.response;
      const roleList = Array.isArray(rawRoles?.data) ? rawRoles.data : (Array.isArray(rawRoles) ? rawRoles : []);
      const groupRoles: RoleData[] = roleList.map((r: any) => ({
        id: String(r.id),
        name: r.name,
        description: r.description || '',
        isSystem: r.is_system_role || false,
        privileges: {}
      }));

      const rawUsers = usersRes?.response ?? usersRes?.result?.response;
      const userList = Array.isArray(rawUsers?.data) ? rawUsers.data : (Array.isArray(rawUsers) ? rawUsers : []);
      const groupMembers: UserData[] = userList.map((u: any) => ({
        id: String(u.id),
        name: `${u.name} ${u.last_name || ''}`.trim(),
        firstName: u.name || '',
        lastName: u.last_name || '',
        email: u.email,
        role: 'viewer',
        status: u.status as any,
        lastLogin: u.created_at || new Date().toISOString(),
        department: 'IT'
      }));

      setGroupRolesById(prev => ({ ...prev, [groupId]: groupRoles }));
      setGroupMembersById(prev => ({ ...prev, [groupId]: groupMembers }));
    } catch (err) {
      console.error(`Error loading details for group ${groupId}:`, err);
    }
  };

  const formatGroupRoles = (group: GroupData): string => {
    const entry = groupRolesById[group.id];
    if (entry !== undefined && entry !== null) {
      if (entry.length === 0) return 'SIN_ROLES_ASIGNADOS';
      return entry.map(r => r.name.toUpperCase()).join(', ');
    }
    if (group.role_names && group.role_names.trim() !== '') {
      return group.role_names.split(';').map(s => s.trim().toUpperCase()).join(', ');
    }
    return 'SIN_ROLES_ASIGNADOS';
  };

  const initialFormStateRef = useRef<Omit<GroupData, 'id'>>({
    name: '',
    description: '',
    roleId: '',
    userIds: []
  });

  const [formData, setFormData] = useState<Omit<GroupData, 'id'>>({
    name: '',
    description: '',
    roleId: roles[0]?.id || '',
    userIds: []
  });

  const isFormDirty = () => {
    const init = initialFormStateRef.current;
    if (formData.name !== init.name) return true;
    if (formData.description !== init.description) return true;
    if (formData.roleId !== init.roleId) return true;
    if (formData.userIds.length !== init.userIds.length) return true;
    if (formData.userIds.some((id, idx) => id !== init.userIds[idx])) return true;
    return false;
  };

  useEffect(() => {
    if (selectedGroup) {
      const current = groups.find(g => g.id === selectedGroup.id);
      if (current) {
        setSelectedGroup(current);
      } else {
        setSelectedGroup(null);
      }
    }
  }, [groups, selectedGroup?.id]);

  useEffect(() => {
    if (selectedGroup && !isFormOpen) {
      const data = {
        name: selectedGroup.name,
        description: selectedGroup.description,
        roleId: selectedGroup.roleId,
        userIds: [...selectedGroup.userIds]
      };
      setFormData(data);
    }
  }, [selectedGroup, isFormOpen]);

  useEffect(() => {
    if (menuCloseSignal > 0) {
      setIsAddUserDropdownOpen(false);
      setIsAddRoleDropdownOpen(false);
    }
  }, [menuCloseSignal]);

  // Desplegables "Añadir Rol" / "Añadir Miembro": se cierran al pulsar fuera de ellos.
  useClickOutside(
    (target) => Boolean(target.closest('.groups-form-add-member-wrapper, .groups-details-add-member-wrapper')),
    () => {
      setIsAddUserDropdownOpen(false);
      setIsAddRoleDropdownOpen(false);
    },
    isAddUserDropdownOpen || isAddRoleDropdownOpen
  );

  // Clic fuera: cierra el panel (con el aviso de cambios sin guardar de
  // siempre). Las zonas con data-keep-detail y los portales no cierran.
  useClickOutside(isDetailKeepZone, () => {
    if (isFormOpen) {
      if (isFormDirty()) {
        setShowDiscardModal(true);
      } else {
        setSelectedGroup(null);
        setIsFormOpen(false);
      }
    } else if (selectedGroup) {
      setSelectedGroup(null);
    }
  }, true);

  const handleCreateNewClick = () => {
    setSelectedGroup(null);
    const initialData = {
      name: '',
      description: '',
      roleId: roles[0]?.id || '',
      userIds: []
    };
    initialFormStateRef.current = initialData;
    setFormData(initialData);
    setIsFormOpen(true);
  };

  const selectGroup = (group: GroupData) => {
    setSelectedGroup(group);
    setIsFormOpen(false);
    loadGroupDetailsIfNeeded(group.id);
  };

  // X del formulario: cierra el panel, avisando si hay cambios.
  const closeForm = () => {
    if (isFormDirty()) {
      setShowDiscardModal(true);
    } else {
      setSelectedGroup(null);
      setIsFormOpen(false);
    }
  };

  const handleEditClick = (group: GroupData) => {
    setSelectedGroup(group);
    const initialData = {
      name: group.name,
      description: group.description,
      roleId: group.roleId,
      userIds: [...group.userIds]
    };
    initialFormStateRef.current = initialData;
    setFormData(initialData);
    setIsFormOpen(true);
    loadGroupDetailsIfNeeded(group.id);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) return;

    try {
      const Cookies = (await import('universal-cookie')).default;
      const cookies = new Cookies();
      const userCookie = cookies.get('101Obex');
      if (!userCookie || !userCookie.token) return;

      const ObexRequestsHandlerP80 = (await import('../../../../handlers/ObexRequestsHandlerP80')).default;

      if (selectedGroup) {
        let res = await ObexRequestsHandlerP80.get('/update_groups', {
          access_token: userCookie.token,
          group_id: selectedGroup.id,
          name: formData.name,
          description: formData.description,
        });
        if (res.data) res = res.data;
        const updated = res?.response ?? res?.result?.response;
        if (!updated || typeof updated !== 'object' || !updated.id) {
          notify('Error', typeof updated === 'string' ? updated : 'No se pudo actualizar el grupo', 'urgent');
          return;
        }

        setGroups(prev => prev.map(g => g.id === selectedGroup.id ? { ...g, name: formData.name, description: formData.description } : g));
        notify('Éxito', 'Grupo actualizado exitosamente', 'info');
      } else {
        // Alta de grupo: solo crea la cabecera (nombre/descripción). El rol y
        // los miembros elegidos en el formulario se asignan aparte con los
        // mismos endpoints que ya usan "Añadir Rol"/"Añadir Miembro" sobre un
        // grupo existente, una vez tenemos el group_id real del backend.
        let res = await ObexRequestsHandlerP80.get('/create_group', {
          access_token: userCookie.token,
          name: formData.name,
          description: formData.description,
        });
        if (res.data) res = res.data;
        const created = res?.response ?? res?.result?.response;
        if (!created || typeof created !== 'object' || !created.id) {
          notify('Error', typeof created === 'string' ? created : 'No se pudo crear el grupo', 'urgent');
          return;
        }

        const newGroupId = String(created.id);

        if (formData.roleId) {
          await ObexRequestsHandlerP80.get('/groups-roles-add', {
            access_token: userCookie.token,
            group_id: newGroupId,
            role_id: formData.roleId,
          });
        }
        for (const userId of formData.userIds) {
          await ObexRequestsHandlerP80.get('/groups-user-add', {
            access_token: userCookie.token,
            group_id: newGroupId,
            user_id: userId,
          });
        }

        const newGroup: GroupData = {
          id: newGroupId,
          name: formData.name,
          description: formData.description,
          roleId: formData.roleId,
          userIds: [...formData.userIds]
        };
        setGroups(prev => [...prev, newGroup]);

        const assignedRole = roles.find(r => r.id === formData.roleId);
        setGroupRolesById(prev => ({ ...prev, [newGroupId]: assignedRole ? [assignedRole] : [] }));
        setGroupMembersById(prev => ({ ...prev, [newGroupId]: users.filter(u => formData.userIds.includes(u.id)) }));

        notify('Éxito', 'Grupo creado exitosamente', 'info');
      }
      setIsFormOpen(false);
    } catch (error) {
      console.error('Error saving group:', error);
      notify('Error', 'Error de red al guardar el grupo', 'urgent');
    }
  };

  const handleDeleteGroup = (groupId: string) => {
    const group = groups.find(g => g.id === groupId);
    if (!group) return;
    setDeleteModalGroup(group);
  };

  const confirmDeleteGroup = async () => {
    if (!deleteModalGroup) return;
    const groupId = deleteModalGroup.id;
    try {
      const Cookies = (await import('universal-cookie')).default;
      const cookies = new Cookies();
      const userCookie = cookies.get('101Obex');
      if (!userCookie || !userCookie.token) return;

      const ObexRequestsHandlerP80 = (await import('../../../../handlers/ObexRequestsHandlerP80')).default;
      let res = await ObexRequestsHandlerP80.get('/delete_group', {
        access_token: userCookie.token,
        group_id: groupId,
      });
      if (res.data) res = res.data;
      const message = res?.response ?? res?.result?.response;
      if (typeof message !== 'string' || !message.toLowerCase().includes('exitosamente')) {
        notify('Error', typeof message === 'string' ? message : 'No se pudo eliminar el grupo', 'urgent');
        return;
      }

      setGroups(prev => prev.filter(g => g.id !== groupId));
      if (selectedGroup?.id === groupId) {
        setSelectedGroup(null);
        setIsFormOpen(false);
      }
      setDeleteModalGroup(null);
      notify('Éxito', 'Grupo eliminado exitosamente', 'info');
    } catch (error) {
      console.error('Error deleting group:', error);
      notify('Error', 'Error de red al eliminar el grupo', 'urgent');
    }
  };

  const handleAddUserToGroup = async (userId: string) => {
    setIsAddUserDropdownOpen(false);

    if (selectedGroup) {
      try {
        const Cookies = (await import('universal-cookie')).default;
        const cookies = new Cookies();
        const userCookie = cookies.get('101Obex');
        if (!userCookie || !userCookie.token) return;

        const ObexRequestsHandlerP80 = (await import('../../../../handlers/ObexRequestsHandlerP80')).default;
        // Registered as "/groups-users-add" but only "/groups-user-add"
        // (singular "user") actually resolves on this backend.
        let res = await ObexRequestsHandlerP80.get('/groups-user-add', {
          access_token: userCookie.token,
          group_id: selectedGroup.id,
          user_id: userId,
        });
        if (res.data) res = res.data;
        const message = res?.response ?? res?.result?.response;
        if (typeof message === 'string' && message.toLowerCase().includes('exitosamente')) {
          const addedUser = users.find(u => u.id === userId);
          if (addedUser) {
            setGroupMembersById(prev => {
              const current = prev[selectedGroup.id] || [];
              if (current.some(u => u.id === userId)) return prev;
              return { ...prev, [selectedGroup.id]: [...current, addedUser] };
            });
            notify('Éxito', 'Usuario añadido exitosamente', 'info');
          }
        } else {
          notify('Error', 'No se pudo añadir el usuario', 'warning');
        }
      } catch (error) {
        console.error('Error adding user to group:', error);
        notify('Error', 'Error de red en la operación', 'urgent');
      }
    } else if (isFormOpen) {
      setFormData(prev => {
        if (!prev.userIds.includes(userId)) {
          return { ...prev, userIds: [...prev.userIds, userId] };
        }
        return prev;
      });
    }
  };

  const handleRemoveUserFromGroup = async (userId: string) => {
    if (selectedGroup) {
      try {
        const Cookies = (await import('universal-cookie')).default;
        const cookies = new Cookies();
        const userCookie = cookies.get('101Obex');
        if (!userCookie || !userCookie.token) return;

        const ObexRequestsHandlerP80 = (await import('../../../../handlers/ObexRequestsHandlerP80')).default;
        let res = await ObexRequestsHandlerP80.get('/groups-users-remove', {
          access_token: userCookie.token,
          group_id: selectedGroup.id,
          user_id: userId,
        });
        if (res.data) res = res.data;
        const message = res?.response ?? res?.result?.response;
        if (typeof message === 'string' && message.toLowerCase().includes('exitosamente')) {
          setGroupMembersById(prev => ({
            ...prev,
            [selectedGroup.id]: (prev[selectedGroup.id] || []).filter(u => u.id !== userId)
          }));
          notify('Éxito', 'Usuario retirado del grupo', 'info');
        } else {
          notify('Error', 'No se pudo retirar al usuario', 'warning');
        }
      } catch (error) {
        console.error('Error removing user from group:', error);
        notify('Error', 'Error de red en la operación', 'urgent');
      }
    } else if (isFormOpen) {
      setFormData(prev => ({
        ...prev,
        userIds: prev.userIds.filter(id => id !== userId)
      }));
    }
  };

  const handleAddRoleToGroup = async (roleId: string) => {
    if (!selectedGroup) return; // assigning a role requires a persisted group_id
    setIsAddRoleDropdownOpen(false);
    try {
      const Cookies = (await import('universal-cookie')).default;
      const cookies = new Cookies();
      const userCookie = cookies.get('101Obex');
      if (!userCookie || !userCookie.token) return;

      const ObexRequestsHandlerP80 = (await import('../../../../handlers/ObexRequestsHandlerP80')).default;
      let res = await ObexRequestsHandlerP80.get('/groups-roles-add', {
        access_token: userCookie.token,
        group_id: selectedGroup.id,
        role_id: roleId,
      });
      if (res.data) res = res.data;

      const message = res?.response ?? res?.result?.response;
      if (typeof message === 'string' && message.toLowerCase().includes('exitosamente')) {
        const role = roles.find(r => r.id === roleId);
        if (role) {
          setGroupRolesById(prev => ({
            ...prev,
            [selectedGroup.id]: [...(prev[selectedGroup.id] || []), role]
          }));
          notify('Éxito', 'Rol asignado exitosamente', 'info');
        }
      } else {
        notify('Error', 'No se pudo asignar el rol', 'warning');
      }
    } catch (error) {
      console.error('Error assigning role to group:', error);
      notify('Error', 'Error de red en la operación', 'urgent');
    }
  };

  const handleRemoveRoleFromGroup = async (roleId: string) => {
    if (!selectedGroup) return; // removing a role requires a persisted group_id
    try {
      const Cookies = (await import('universal-cookie')).default;
      const cookies = new Cookies();
      const userCookie = cookies.get('101Obex');
      if (!userCookie || !userCookie.token) return;

      const ObexRequestsHandlerP80 = (await import('../../../../handlers/ObexRequestsHandlerP80')).default;
      let res = await ObexRequestsHandlerP80.get('/groups-roles-remove', {
        access_token: userCookie.token,
        group_id: selectedGroup.id,
        role_id: roleId,
      });
      if (res.data) res = res.data;

      const message = res?.response ?? res?.result?.response;
      if (typeof message === 'string' && message.toLowerCase().includes('exitosamente')) {
        setGroupRolesById(prev => ({
          ...prev,
          [selectedGroup.id]: (prev[selectedGroup.id] || []).filter(r => r.id !== roleId)
        }));
        notify('Éxito', 'Rol retirado exitosamente', 'info');
      } else {
        notify('Error', 'No se pudo retirar el rol', 'warning');
      }
    } catch (error) {
      console.error('Error removing role from group:', error);
      notify('Error', 'Error de red en la operación', 'urgent');
    }
  };

  // The backend already filters by `search`, so no local filtering needed.
  const filteredGroups = groups;

  // An existing group's members come from GET /groups-users-list; a brand-new
  // group being created (no group_id yet) only has the local formData picks.
  const groupMembersEntry = selectedGroup ? groupMembersById[selectedGroup.id] : undefined;

  const renderMembersSkeleton = () => (
    <>
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="groups-details-member-card">
          <div className="groups-details-member-card-info">
            <Skeleton width="60%" height="1.2em" />
            <Skeleton width="80%" height="1.2em" />
            <div className="groups-details-member-card-tags">
              <Skeleton width="72px" height="1.2em" />
              <Skeleton width="56px" height="1.2em" />
            </div>
          </div>
          <Skeleton width="20px" height="20px" />
        </div>
      ))}
    </>
  );

  const renderRoleChipsSkeleton = () => (
    <div className="groups-details-role-chips">
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={i} width="88px" height="1.4em" />
      ))}
    </div>
  );
  const activeMembers: UserData[] = selectedGroup
    ? (groupMembersEntry || [])
    : users.filter(u => formData.userIds.includes(u.id));
  const availableUsers = users.filter(u => !activeMembers.some(m => m.id === u.id));

  const columns: ColumnDef<GroupData>[] = [
    {
      id: 'id',
      header: 'ID_REF',
      render: (group) => <span className="groups-table-id">{group.id.toUpperCase()}</span>
    },
    {
      id: 'name',
      header: 'Nombre',
      render: (group) => <span className="groups-table-name-text">{group.name}</span>
    },
    {
      id: 'description',
      header: 'Descripción',
      render: (group) => (
        <span className="groups-table-desc" title={group.description}>
          {group.description}
        </span>
      )
    },
    {
      id: 'role',
      header: 'Roles del Grupo',
      render: (group) => {
        const roleName = formatGroupRoles(group);
        return (
          <span className="groups-table-role">
            {roleName}
          </span>
        );
      }
    },
    {
      id: 'members',
      header: 'Miembros',
      render: (group) => {
        const entry = groupMembersById[group.id];
        const count = entry !== undefined && entry !== null ? entry.length : (group.members_count ?? 0);
        return (
          <span className="groups-table-members-count">
            {count}
          </span>
        );
      },
      align: 'center'
    }
  ];

  const renderActions = (group: GroupData) => (
    <RowActionsMenu
      onOpenChange={(open) => {
        if (open) {
          setIsAddUserDropdownOpen(false);
          setIsAddRoleDropdownOpen(false);
        }
      }}
      actions={[
        viewAction(() => selectGroup(group)),
        editAction(() => handleEditClick(group)),
        deleteAction(() => handleDeleteGroup(group.id))
      ]}
    />
  );

  return (
    <>
      <MasterDetailLayout isOpen={Boolean(selectedGroup || isFormOpen)} drawer>
        <div className="groups-view-container">
          <div className="groups-view-header-container">
            <ModuleHeader
              title="Gestión_de_Grupos_Trabajo"
              subtitle="Agrupe al personal en equipos y asígneles roles: cada miembro hereda automáticamente los permisos del equipo al que pertenece."
            />
            <div className="groups-view-header-actions">
              <button onClick={handleCreateNewClick} className="groups-view-create-btn" data-keep-detail>
                <Plus size={14} />
                Nuevo_Grupo
              </button>
            </div>
          </div>

          <div className="groups-view-filters-container">
            <TableSearch 
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="BUSCAR GRUPOS POR NOMBRE O DESCRIPCIÓN..."
            />
          </div>

          <DataTable
            data={filteredGroups}
            columns={columns}
            title="Grupos_de_Trabajo_Registrados"
            subtitle={`TOTAL: ${totalRecords}`}
            showPagination={true}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            isLoading={groupsLoading}
            totalRecords={totalRecords}
            getRowId={(group) => group.id}
            onRowClick={selectGroup}
            selectedRowId={selectedGroup?.id}
            actionsColumn={renderActions}
            emptyText="No hay grupos registrados en el sistema."
          />
        </div>

        {isFormOpen ? (
          <SidebarForm
            drawer
            title={selectedGroup ? `EDITAR GRUPO: ${selectedGroup.name}` : 'NUEVO GRUPO'}
            onClose={closeForm}
            submitLabel="Guardar Cambios"
            onSubmit={handleSave}
            showDelete={Boolean(selectedGroup)}
            onDelete={() => selectedGroup && handleDeleteGroup(selectedGroup.id)}
          >
            <div className="sidebar-form-grid-2">
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Nombre del Grupo</label>
                <input
                  required
                  className="sidebar-form-input"
                  style={{ textTransform: 'uppercase' }}
                  placeholder="EJ. SOPORTE DE SISTEMAS"
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFormData(prev => ({ ...prev, name: val }));
                  }}
                />
              </div>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Rol de Seguridad Asignado</label>
                {roles.length > 0 ? (
                  <select
                    className="sidebar-form-input"
                    value={formData.roleId}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData(prev => ({ ...prev, roleId: val }));
                    }}
                  >
                    {roles.map(r => (
                      <option key={r.id} value={r.id} className="groups-form-option">
                        {r.name.toUpperCase()} (Privilegios de {r.name})
                      </option>
                    ))}
                  </select>
                ) : (
                  <select className="sidebar-form-input groups-form-input-error" disabled title="No se pudieron cargar los roles">
                    <option>ROLES_NO_DISPONIBLES</option>
                  </select>
                )}
              </div>
            </div>

            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Descripción del Grupo</label>
              <textarea
                className="sidebar-form-input groups-form-textarea"
                placeholder="Ej. Personal con acceso general al panel de ventas, catálogo editorial y gestión de stock."
                value={formData.description}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData(prev => ({ ...prev, description: val }));
                }}
              />
            </div>

            <div className="groups-form-members-section">
              <div className="groups-form-members-header">
                <span className="groups-form-members-title">
                  Miembros del Grupo ({activeMembers.length})
                </span>
                <div className="groups-form-add-member-wrapper">
                  <button
                    type="button"
                    disabled={usersError}
                    title={usersError ? 'No se pudieron cargar los usuarios' : undefined}
                    className="groups-form-add-member-btn"
                    onClick={(e) => { e.stopPropagation(); if (usersError) return; setIsAddUserDropdownOpen(!isAddUserDropdownOpen); }}
                  >
                    <UserPlus size={10} />
                    {usersError ? 'Usuarios_No_Disponibles' : 'Añadir Miembro'}
                  </button>
                  {isAddUserDropdownOpen && !usersError && (
                    <div className="groups-form-add-member-menu" onClick={(e) => e.stopPropagation()}>
                      {usersLoading ? (
                        <div className="groups-form-add-member-empty">Cargando usuarios…</div>
                      ) : availableUsers.length === 0 ? (
                        <div className="groups-form-add-member-empty">Todos los usuarios ya están en este grupo</div>
                      ) : (
                        availableUsers.map(u => (
                          <button
                            key={u.id}
                            type="button"
                            onClick={() => handleAddUserToGroup(u.id)}
                            className="groups-form-add-member-item"
                          >
                            <div className="groups-form-add-member-item-info">
                              <span className="groups-form-add-member-item-name">{u.name}</span>
                              <span className="groups-form-add-member-item-email">{u.email}</span>
                            </div>
                            <Plus size={10} className="groups-form-add-member-item-icon" />
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="groups-form-members-grid custom-scrollbar">
                {selectedGroup && groupMembersEntry === undefined ? (
                  renderMembersSkeleton()
                ) : selectedGroup && groupMembersEntry === null ? (
                  <div className="groups-details-members-grid-empty groups-details-members-grid-error" style={{ gridColumn: '1 / -1' }}>
                    No se pudieron cargar los miembros de este grupo.
                  </div>
                ) : activeMembers.length === 0 ? (
                  <div className="groups-details-members-grid-empty" style={{ gridColumn: '1 / -1' }}>
                    No hay miembros asignados a este grupo.
                  </div>
                ) : (
                  activeMembers.map(u => (
                    <div key={u.id} className="groups-form-member-card">
                      <div className="groups-form-member-info">
                        <span className="groups-form-member-name">{u.name}</span>
                        <span className="groups-form-member-email">{u.email}</span>
                      </div>
                      <button
                        type="button"
                        className="groups-form-member-remove"
                        title="Remover del grupo"
                        onClick={() => handleRemoveUserFromGroup(u.id)}
                      >
                        <UserMinus size={12} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </SidebarForm>
        ) : selectedGroup ? (
          <DetailPanel
            drawer
            title="Ficha_De_Grupo"
            onClose={() => { setSelectedGroup(null); setIsFormOpen(false); }}
            headerActions={
              <DetailHeaderButton icon={Edit3} label="Editar" onClick={() => handleEditClick(selectedGroup)} />
            }
          >
            <DetailIdentity
              icon={Layers}
              reference="// GRUPO_DE_TRABAJO"
              name={selectedGroup.name}
              subtitle={selectedGroup.description}
            />

            {/* Inherited Role Info Box */}
            <div className="groups-details-inherited-role-box">
              <div className="groups-details-role-info">
                <Shield size={16} className="groups-details-role-icon" />
                <div>
                  <span className="groups-details-role-name">
                    Roles Asignados al Grupo:
                  </span>
                  <p className="groups-details-role-caption">
                    Los miembros de este grupo heredan automáticamente estos permisos.
                  </p>
                  {(() => {
                    const assignedRoles = groupRolesById[selectedGroup.id];
                    if (assignedRoles === undefined) {
                      return renderRoleChipsSkeleton();
                    }
                    if (assignedRoles === null) {
                      return <p className="groups-details-role-desc groups-details-role-desc-error">No se pudieron cargar los roles de este grupo.</p>;
                    }
                    if (assignedRoles.length === 0) {
                      return <p className="groups-details-role-desc">Sin roles asignados.</p>;
                    }
                    return (
                      <div className="groups-details-role-chips">
                        {assignedRoles.map(role => (
                          <span key={role.id} className="groups-details-role-chip">
                            {role.name.toUpperCase()}
                            <button
                              type="button"
                              onClick={() => handleRemoveRoleFromGroup(role.id)}
                              className="groups-details-role-chip-remove"
                              title="Retirar rol del grupo"
                            >
                              <X size={10} />
                            </button>
                          </span>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>

              <div className="groups-details-add-member-wrapper">
                <button
                  type="button"
                  disabled={rolesError}
                  title={rolesError ? 'No se pudieron cargar los roles' : undefined}
                  onClick={(e) => { e.stopPropagation(); if (rolesError) return; setIsAddRoleDropdownOpen(!isAddRoleDropdownOpen); }}
                  className="groups-details-add-member-btn"
                >
                  <Plus size={10} />
                  {rolesError ? 'Roles_No_Disponibles' : 'Añadir Rol'}
                </button>
                {isAddRoleDropdownOpen && !rolesError && (() => {
                  const assignedRoleIds = new Set((groupRolesById[selectedGroup.id] || []).map(r => r.id));
                  const availableRoles = roles.filter(r => !assignedRoleIds.has(r.id));
                  return (
                    <div className="groups-details-add-member-menu" onClick={(e) => e.stopPropagation()}>
                      {rolesLoading ? (
                        <div className="groups-details-add-member-empty">Cargando roles…</div>
                      ) : availableRoles.length === 0 ? (
                        <div className="groups-details-add-member-empty">
                          Todos los roles ya están asignados a este grupo
                        </div>
                      ) : (
                        availableRoles.map(role => (
                          <button
                            key={role.id}
                            type="button"
                            onClick={() => handleAddRoleToGroup(role.id)}
                            className="groups-details-add-member-item"
                          >
                            <div className="groups-details-add-member-item-info">
                              <span className="groups-details-add-member-item-name">{role.name}</span>
                              <span className="groups-details-add-member-item-email">{role.description}</span>
                            </div>
                            <Plus size={10} className="groups-details-add-member-item-icon" />
                          </button>
                        ))
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Members Management */}
            <div className="groups-details-members-section">
              <div className="groups-details-members-header">
                <span className="groups-details-members-title">
                  Miembros Asociados ({activeMembers.length})
                </span>

                {/* Add Member Dropdown */}
                <div className="groups-details-add-member-wrapper">
                  <button
                    type="button"
                    disabled={usersError}
                    title={usersError ? 'No se pudieron cargar los usuarios' : undefined}
                    onClick={(e) => { e.stopPropagation(); if (usersError) return; setIsAddUserDropdownOpen(!isAddUserDropdownOpen); }}
                    className="groups-details-add-member-btn"
                  >
                    <UserPlus size={10} />
                    {usersError ? 'Usuarios_No_Disponibles' : 'Asociar Personal'}
                  </button>
                  {isAddUserDropdownOpen && !usersError && (
                    <div className="groups-details-add-member-menu" onClick={(e) => e.stopPropagation()}>
                      {usersLoading ? (
                        <div className="groups-details-add-member-empty">Cargando usuarios…</div>
                      ) : availableUsers.length === 0 ? (
                        <div className="groups-details-add-member-empty">
                          Todos los usuarios ya están en el grupo
                        </div>
                      ) : (
                        availableUsers.map(user => (
                          <button
                            key={user.id}
                            type="button"
                            onClick={() => handleAddUserToGroup(user.id)}
                            className="groups-details-add-member-item"
                          >
                            <div className="groups-details-add-member-item-info">
                              <span className="groups-details-add-member-item-name">{user.name}</span>
                              <span className="groups-details-add-member-item-email">{user.email}</span>
                            </div>
                            <Plus size={10} className="groups-details-add-member-item-icon" />
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="groups-details-members-grid custom-scrollbar">
                {groupMembersEntry === undefined ? (
                  renderMembersSkeleton()
                ) : groupMembersEntry === null ? (
                  <div className="groups-details-members-grid-empty groups-details-members-grid-error">
                    No se pudieron cargar los miembros de este grupo.
                  </div>
                ) : activeMembers.length === 0 ? (
                  <div className="groups-details-members-grid-empty">
                    No hay usuarios registrados en este grupo. Utilice "Asociar Personal" para incluir usuarios.
                  </div>
                ) : (
                  activeMembers.map(user => (
                    <div
                      key={user.id}
                      className="groups-details-member-card"
                    >
                      <div className="groups-details-member-card-info">
                        <span className="groups-details-member-card-name">{user.name}</span>
                        <span className="groups-details-member-card-email">{user.email}</span>
                        <div className="groups-details-member-card-tags">
                          <span className="groups-details-member-card-tag department">
                            Dpto: {user.department || 'N/A'}
                          </span>
                          <span className={`groups-details-member-card-tag status ${user.status === 'active' ? 'active' : 'inactive'}`}>
                            {user.status}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveUserFromGroup(user.id)}
                        className="groups-details-member-card-remove"
                        title="Retirar de este grupo"
                      >
                        <UserMinus size={13} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </DetailPanel>
        ) : null}
      </MasterDetailLayout>

      <Modal
        isOpen={!!deleteModalGroup}
        type="urgent"
        title="Eliminar grupo"
        message={`¿Está seguro de que desea eliminar el grupo de usuarios "${deleteModalGroup?.name}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={confirmDeleteGroup}
        onCancel={() => setDeleteModalGroup(null)}
      />

      <Modal
        isOpen={showDiscardModal}
        type="warning"
        title="Descartar cambios"
        message="¿Desea descartar los cambios no guardados y salir del formulario, o prefiere continuar editando?"
        confirmText="Descartar y salir"
        cancelText="Continuar editando"
        onConfirm={() => {
          setShowDiscardModal(false);
          setSelectedGroup(null);
          setIsFormOpen(false);
        }}
        onCancel={() => setShowDiscardModal(false)}
      />
    </>
  );
};
